import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import Select2 from "../../Components/Accounting/Select2";
import Select from "react-select";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import { canAddRow, addRowBlockedReason } from "../../util/accounting/formRows";
import toast, { Toaster } from "react-hot-toast";

// Contra entry: only cash/bank accounts on both sides
// Expense entry: debit = expense accounts, credit = cash/bank
// Income entry: debit = cash/bank, credit = income accounts
// General: all postable accounts

const ACCOUNT_TYPE_RESTRICTIONS = {
  contra: {
    debit: ["asset"], // cash/bank type filtered further by code prefix
    credit: ["asset"],
    info: "Contra entries transfer funds between cash/bank accounts only.",
    debitFilter: (acc) => ["1010", "1011", "1015", "1020", "1021", "1025"].some((p) => acc.account_code?.startsWith(p)) || acc.sub_type === "bank" || acc.sub_type === "cash",
    creditFilter: (acc) => ["1010", "1011", "1015", "1020", "1021", "1025"].some((p) => acc.account_code?.startsWith(p)) || acc.sub_type === "bank" || acc.sub_type === "cash",
  },
  expense: {
    debit: ["expense"],
    credit: ["asset"],
    info: "Expense entries: debit an expense account, credit a cash/bank account.",
    debitFilter: (acc) => acc.type === "expense",
    creditFilter: (acc) => acc.type === "asset" && (acc.account_code?.startsWith("1") || acc.sub_type === "bank" || acc.sub_type === "cash"),
  },
  income: {
    debit: ["asset"],
    credit: ["revenue", "income"],
    info: "Income entries: debit cash/bank, credit a revenue/income account.",
    debitFilter: (acc) => acc.type === "asset" && (acc.account_code?.startsWith("1") || acc.sub_type === "bank" || acc.sub_type === "cash"),
    creditFilter: (acc) => acc.type === "revenue" || acc.type === "income",
  },
  depreciation: {
    debit: ["expense"],
    credit: ["asset", "contra_asset"],
    info: "Depreciation: debit depreciation expense, credit accumulated depreciation.",
    debitFilter: (acc) => acc.type === "expense" && acc.name?.toLowerCase().includes("depreciation"),
    creditFilter: (acc) => acc.type === "asset" || acc.account_code?.startsWith("1700"),
  },
  appreciation: {
    debit: ["asset"],
    credit: ["equity", "revenue"],
    info: "Appreciation: debit the asset account, credit revaluation reserve or other equity.",
    debitFilter: (acc) => acc.type === "asset",
    creditFilter: (acc) => acc.type === "equity" || acc.type === "revenue",
  },
  general: {
    debit: null,
    credit: null,
    info: "General (unrestricted) journal entry. Both debit and credit can use any postable account.",
    debitFilter: () => true,
    creditFilter: () => true,
  },
};

const emptyLine = (type = "debit") => ({ account_id: "", debit: "", credit: "", description: "", type });

/** A journal line is only an entry once it has an account and an amount. */
const JE_LINE_REQUIRED = [
  { key: "account_id", label: "an account" },
  { key: "amount", label: "an amount", numeric: true },
];

/** A line carries a debit or a credit, never both. */
const withAmount = (rows) =>
  rows.map((l) => ({ ...l, amount: (parseFloat(l.debit) || 0) || (parseFloat(l.credit) || 0) }));


const NewJournalEntry = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const entryType = searchParams.get("type") || "general";
  const restriction = ACCOUNT_TYPE_RESTRICTIONS[entryType] || ACCOUNT_TYPE_RESTRICTIONS.general;
  const editId = searchParams.get("edit");
  const isEditMode = !!editId;

  const [financialYears, setFinancialYears] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedFY, setSelectedFY] = useState("");
  const [form, setForm] = useState({ entry_date: new Date().toISOString().split("T")[0], reference: "", description: "", narration: "", currency_id: "" });
  const [contactOpt, setContactOpt] = useState(null);
  const [projectOpt, setProjectOpt] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [lines, setLines] = useState([emptyLine("debit"), emptyLine("credit")]);
  const [saving, setSaving] = useState(false);
  const [posting, setPosting] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(isEditMode);
  const [recentEntries, setRecentEntries] = useState([]);
  const [recentLoading, setRecentLoading] = useState(true);

  useEffect(() => {
    fetchFinancialYears();
    fetchAccounts();
    fetchContacts();
    fetchProjects();
    fetchRecentEntries();
  }, []);

  useEffect(() => {
    if (!editId) return;
    (async () => {
      setLoadingEdit(true);
      const res = await ajaxAccounting.getJournal({ id: parseInt(editId) });
      if (res.status === "OK" && res.details) {
        const je = res.details;
        if (je.status !== "draft") {
          toast.error("Only draft entries can be edited. Use Reverse for posted entries.");
          navigate(`/accounting/journal/${editId}`);
          return;
        }
        setForm({
          entry_date: je.entry_date,
          reference: je.reference || "",
          description: je.description || "",
          narration: je.narration || "",
          currency_id: "",
        });
        setSelectedFY(String(je.financial_year_id));
        setLines((je.lines || []).map((l) => ({
          account_id: String(l.account_id),
          debit: l.debit > 0 ? String(l.debit) : "",
          credit: l.credit > 0 ? String(l.credit) : "",
          description: l.description || "",
          type: l.debit > 0 ? "debit" : "credit",
        })));
      } else {
        toast.error(res.message || "Journal entry not found.");
        navigate("/accounting/journal");
      }
      setLoadingEdit(false);
    })();
  }, [editId]);

  const fetchRecentEntries = async () => {
    setRecentLoading(true);
    const res = await ajaxAccounting.listJournals({});
    if (res.status === "OK") setRecentEntries((res.details?.list || []).slice(0, 6));
    setRecentLoading(false);
  };

  const handleDeleteRecent = async (id) => {
    if (!window.confirm("Delete this draft journal entry? This cannot be undone.")) return;
    const res = await ajaxAccounting.deleteJournal({ id });
    if (res.status === "OK") { toast.success("Journal entry deleted."); fetchRecentEntries(); }
    else toast.error(res.message || "Failed to delete");
  };

  const fetchFinancialYears = async () => {
    const res = await ajaxAccounting.listFinancialYears({});
    if (res.status === "OK") {
      setFinancialYears(res.details || []);
      const cur = (res.details || []).find((y) => y.is_current === 1);
      setSelectedFY(String(cur?.id || res.details?.[0]?.id || ""));
    }
  };

  const fetchAccounts = async () => {
    const res = await ajaxAccounting.listAccounts({ postable_only: 1 });
    if (res.status === "OK") setAccounts(res.details || []);
  };

  const debitAccounts = accounts.filter((a) => restriction.debitFilter ? restriction.debitFilter(a) : true);
  const creditAccounts = accounts.filter((a) => restriction.creditFilter ? restriction.creditFilter(a) : true);

  const toSelectOptions = (accs) => accs.map((a) => ({ value: String(a.id), label: `${a.account_code} — ${a.name}` }));

  const setLine = (i, field, val) => {
    const updated = [...lines];
    updated[i] = { ...updated[i], [field]: val };
    // Enforce that debit and credit are mutually exclusive per line
    if (field === "debit" && val) updated[i].credit = "";
    if (field === "credit" && val) updated[i].debit = "";
    setLines(updated);
  };

  const addDebitLine = () => setLines([...lines, emptyLine("debit")]);
  const addCreditLine = () => setLines([...lines, emptyLine("credit")]);
  const removeLine = (i) => { if (lines.length > 2) setLines(lines.filter((_, idx) => idx !== i)); };

  const totalDebit = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  const fetchContacts = async () => {
    const res = await ajaxAccounting.listContacts({});
    if (res.status === "OK") setContacts(Array.isArray(res.details) ? res.details : res.details?.list || []);
  };

  const fetchProjects = async () => {
    const res = await ajaxAccounting.listProjects({ status: "active" });
    if (res.status === "OK") setProjects(Array.isArray(res.details) ? res.details : res.details?.list || []);
  };

  const buildPayload = () => ({
    entry_date: form.entry_date,
    reference: form.reference,
    description: form.description,
    narration: form.narration,
    financial_year_id: parseInt(selectedFY),
    type: entryType,
    contact_id: contactOpt?.value || null,
    project_id: projectOpt?.value || null,
    lines: lines.filter((l) => l.account_id).map((l) => ({
      account_id: parseInt(l.account_id),
      debit: parseFloat(l.debit) || 0,
      credit: parseFloat(l.credit) || 0,
      description: l.description,
    })),
  });

  const validate = () => {
    if (!selectedFY) return "Select a financial year.";
    if (!form.entry_date) return "Entry date is required.";
    if (!form.description) return "Description is required.";
    const validLines = lines.filter((l) => l.account_id && (parseFloat(l.debit) || parseFloat(l.credit)));
    if (validLines.length < 2) return "At least 2 valid journal lines required.";
    if (!isBalanced) return "Debits and credits must balance.";
    return null;
  };

  const handleSaveDraft = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    const res = await ajaxAccounting.createJournal(buildPayload());
    if (res.status === "OK") { toast.success("Journal saved as draft!"); navigate("/accounting/journal"); }
    else toast.error(res.message || "Failed to save");
    setSaving(false);
  };

  const handleSaveAndPost = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setPosting(true);
    const res = await ajaxAccounting.createAndPostJournal(buildPayload());
    if (res.status === "OK") { toast.success("Journal posted to ledger!"); navigate("/accounting/journal"); }
    else toast.error(res.message || "Failed to post");
    setPosting(false);
  };

  const handleUpdate = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    const res = await ajaxAccounting.updateJournal({ id: parseInt(editId), ...buildPayload() });
    if (res.status === "OK") { toast.success("Journal entry updated!"); navigate(`/accounting/journal/${editId}`); }
    else toast.error(res.message || "Failed to update");
    setSaving(false);
  };

  const ENTRY_TITLES = { general: "General Journal Entry", contra: "Contra Entry", expense: "Expense Entry", income: "Income Entry", depreciation: "Depreciation Entry", appreciation: "Asset Appreciation" };

  if (loadingEdit) {
    return (
      <AppContainer title="Edit Journal Entry" previousPageTitle="Journal Entries">
        <div className="text-center py-5"><i className="fa-solid fa-spinner fa-spin fa-2x tx-primary"></i></div>
      </AppContainer>
    );
  }

  return (
    <AppContainer
      title={isEditMode ? "Edit Journal Entry" : (ENTRY_TITLES[entryType] || "Journal Entry")}
      previousPageTitle="Journal Entries"
      description={restriction.info}
      actions={isEditMode ? [
        { label: saving ? "Updating..." : "Update Entry", icon: "fa-solid fa-floppy-disk", className: "btn btn-primary", onClick: handleUpdate, disabled: saving },
        { label: "Cancel", icon: "fa-solid fa-xmark", className: "btn btn-light", onClick: () => navigate(`/accounting/journal/${editId}`) },
      ] : [
        { label: "Save Draft", icon: "fa-solid fa-floppy-disk", className: "btn btn-outline-secondary", onClick: handleSaveDraft, disabled: saving },
        { label: posting ? "Posting..." : "Save & Post", icon: "fa-solid fa-check-double", className: "btn btn-primary", onClick: handleSaveAndPost, disabled: posting || saving },
        { label: "Cancel", icon: "fa-solid fa-xmark", className: "btn btn-light", onClick: () => navigate("/accounting/journal") },
      ]}
    >
      <Toaster />

      {restriction.info && (
        <div className="alert alert-info mb-3 d-flex align-items-center gap-2" style={{ fontSize: "0.85rem" }}>
          <i className="fa-solid fa-info-circle"></i>
          <span>{restriction.info}</span>
        </div>
      )}

      <div className="row">
        <div className="col-lg-8">
          <div className="card custom-card p-4 mb-3">
            <h6 className="fw-semibold mb-3">Entry Details</h6>
            <div className="row g-3">
              <div className="col-lg-6">
                <label className="form-label tx-12 fw-semibold">Financial Year <span className="tx-danger">*</span></label>
                <Select2 options={financialYears.map((y) => ({ value: String(y.id), label: y.name }))} value={selectedFY} onChange={setSelectedFY} placeholder="Select Year" />
              </div>
              <div className="col-lg-6">
                <label className="form-label tx-12 fw-semibold">Entry Date <span className="tx-danger">*</span></label>
                <input type="date" className="form-control" value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} />
              </div>
              <div className="col-lg-6">
                <label className="form-label tx-12 fw-semibold">Reference</label>
                <input type="text" className="form-control" placeholder="e.g. REF-001" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
              </div>
              <div className="col-lg-6">
                <label className="form-label tx-12 fw-semibold">Description <span className="tx-danger">*</span></label>
                <input type="text" className="form-control" placeholder="Entry description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="col-12">
                <label className="form-label tx-12 fw-semibold">Narration / Notes</label>
                <textarea className="form-control" rows={2} placeholder="Additional notes..." value={form.narration} onChange={(e) => setForm({ ...form, narration: e.target.value })} />
              </div>
              <div className="col-lg-6">
                <label className="form-label tx-12 fw-semibold">Contact (Customer / Supplier)</label>
                <Select
                  options={contacts.map((c) => ({ value: c.id, label: `${c.name} (${c.type})` }))}
                  value={contactOpt}
                  onChange={setContactOpt}
                  placeholder="Link to contact (optional)"
                  isClearable isSearchable
                />
              </div>
              <div className="col-lg-6">
                <label className="form-label tx-12 fw-semibold">Project / Cost Centre</label>
                <Select
                  options={projects.map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
                  value={projectOpt}
                  onChange={setProjectOpt}
                  placeholder="Link to project (optional)"
                  isClearable isSearchable
                />
              </div>
            </div>
          </div>

          {/* Journal Lines */}
          <div className="card custom-card p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-semibold mb-0">Journal Lines</h6>
              <div className="d-flex gap-2">
                <button className="btn btn-sm btn-outline-success" onClick={addDebitLine}
                  disabled={!canAddRow(withAmount(lines), JE_LINE_REQUIRED)}
                  title={addRowBlockedReason(withAmount(lines), JE_LINE_REQUIRED) || "Add a debit line"}><i className="fa-solid fa-plus me-1"></i>Add Dr Line</button>
                <button className="btn btn-sm btn-outline-danger" onClick={addCreditLine}
                  disabled={!canAddRow(withAmount(lines), JE_LINE_REQUIRED)}
                  title={addRowBlockedReason(withAmount(lines), JE_LINE_REQUIRED) || "Add a credit line"}><i className="fa-solid fa-plus me-1"></i>Add Cr Line</button>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-bordered" style={{ fontSize: "0.85rem" }}>
                <thead className="bg-light">
                  <tr>
                    <th style={{ width: "50px" }}>Type</th>
                    <th style={{ minWidth: "240px" }}>Account</th>
                    <th style={{ minWidth: "130px" }}>Debit (Dr)</th>
                    <th style={{ minWidth: "130px" }}>Credit (Cr)</th>
                    <th>Description</th>
                    <th style={{ width: "40px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, i) => {
                    const isDebit = line.type !== "credit";
                    return (
                    <tr key={i}>
                      <td className="text-center align-middle">
                        <span className={`badge ${isDebit ? "bg-success" : "bg-danger"}`}>{isDebit ? "DR" : "CR"}</span>
                      </td>
                      <td>
                        <Select2
                          id={`line-account-${i}`}
                          options={toSelectOptions(isDebit ? debitAccounts : creditAccounts)}
                          value={line.account_id}
                          onChange={(v) => setLine(i, "account_id", v)}
                          placeholder={entryType === "general" ? "Account" : (isDebit ? "Debit Account" : "Credit Account")}
                        />
                      </td>
                      <td>
                        <input type="number" className="form-control form-control-sm" placeholder="0.00" min="0" step="0.01"
                          value={line.debit} disabled={!isDebit} onChange={(e) => setLine(i, "debit", e.target.value)} />
                      </td>
                      <td>
                        <input type="number" className="form-control form-control-sm" placeholder="0.00" min="0" step="0.01"
                          value={line.credit} disabled={isDebit} onChange={(e) => setLine(i, "credit", e.target.value)} />
                      </td>
                      <td>
                        <input type="text" className="form-control form-control-sm" placeholder="Line description"
                          value={line.description} onChange={(e) => setLine(i, "description", e.target.value)} />
                      </td>
                      <td>
                        {lines.length > 2 && (
                          <button className="btn btn-sm btn-light tx-danger" onClick={() => removeLine(i)}>
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-light">
                  <tr>
                    <td className="fw-bold text-end">Totals</td>
                    <td className="fw-bold tx-success">{functions.formatCurrency(totalDebit)}</td>
                    <td className="fw-bold tx-danger">{functions.formatCurrency(totalCredit)}</td>
                    <td colSpan={2}>
                      {!isBalanced && (
                        <span className="badge bg-danger ms-2">
                          Out of balance by {functions.formatCurrency(Math.abs(totalDebit - totalCredit))}
                        </span>
                      )}
                      {isBalanced && lines.some((l) => l.account_id) && (
                        <span className="badge bg-success ms-2"><i className="fa-solid fa-check me-1"></i>Balanced</span>
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="card custom-card p-3 mb-3">
            <h6 className="fw-semibold mb-3">Entry Summary</h6>
            <div className="d-flex justify-content-between mb-2">
              <span className="tx-13 tx-muted">Entry Type</span>
              <span className="badge bg-primary">{ENTRY_TITLES[entryType] || entryType}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span className="tx-13 tx-muted">Total Debit</span>
              <span className="fw-bold tx-success">{functions.formatCurrency(totalDebit)}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span className="tx-13 tx-muted">Total Credit</span>
              <span className="fw-bold tx-danger">{functions.formatCurrency(totalCredit)}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span className="tx-13 tx-muted">Difference</span>
              <span className={`fw-bold ${isBalanced ? "tx-success" : "tx-danger"}`}>{functions.formatCurrency(Math.abs(totalDebit - totalCredit))}</span>
            </div>
            <hr />
            <div className={`alert ${isBalanced && lines.some((l) => l.account_id) ? "alert-success" : "alert-warning"} p-2 tx-13`}>
              {isBalanced && lines.some((l) => l.account_id) ? "✓ Entry is balanced and ready to post." : "⚠ Entry must be balanced to post."}
            </div>
          </div>

          <div className="card custom-card p-3">
            <h6 className="fw-semibold mb-2">Account Restriction</h6>
            <div className="tx-13 tx-muted">
              {entryType === "general" && <p>No restriction — any postable account can be used.</p>}
              {entryType === "contra" && <><p><strong>Debit:</strong> Cash / Bank accounts only</p><p><strong>Credit:</strong> Cash / Bank accounts only</p></>}
              {entryType === "expense" && <><p><strong>Debit:</strong> Expense accounts</p><p><strong>Credit:</strong> Cash / Bank accounts</p></>}
              {entryType === "income" && <><p><strong>Debit:</strong> Cash / Bank accounts</p><p><strong>Credit:</strong> Revenue / Income accounts</p></>}
              {entryType === "depreciation" && <><p><strong>Debit:</strong> Depreciation expense</p><p><strong>Credit:</strong> Accumulated depreciation</p></>}
              {entryType === "appreciation" && <><p><strong>Debit:</strong> Asset account</p><p><strong>Credit:</strong> Revaluation reserve / Equity</p></>}
            </div>
          </div>
        </div>
      </div>

      {!isEditMode && (
        <div className="card custom-card p-3 mt-3">
          <h6 className="fw-semibold mb-3">Recent Entries</h6>
          {recentLoading ? (
            <div className="text-center py-3"><i className="fa-solid fa-spinner fa-spin tx-primary"></i></div>
          ) : recentEntries.length === 0 ? (
            <div className="tx-13 tx-muted">No journal entries yet.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm" style={{ fontSize: "0.85rem" }}>
                <thead className="bg-light">
                  <tr>
                    <th>Reference</th>
                    <th>Date</th>
                    <th>Description</th>
                    <th className="text-end">Amount</th>
                    <th>Status</th>
                    <th style={{ width: "110px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {recentEntries.map((je) => (
                    <tr key={je.id}>
                      <td className="fw-semibold">{je.reference}</td>
                      <td>{functions.formatDate(je.entry_date)}</td>
                      <td className="tx-muted">{je.description}</td>
                      <td className="text-end">{functions.formatCurrency(je.total_debit)}</td>
                      <td>
                        <span className={`badge bg-${je.status === "posted" ? "success" : je.status === "void" ? "secondary" : "warning"}`}>{je.status}</span>
                      </td>
                      <td>
                        <button className="btn btn-sm btn-light" title="View" onClick={() => navigate(`/accounting/journal/${je.id}`)}>
                          <i className="fa-solid fa-eye"></i>
                        </button>
                        {je.status === "draft" && (
                          <>
                            <button className="btn btn-sm btn-light tx-primary ms-1" title="Edit" onClick={() => navigate(`/accounting/journal/new?type=${je.type}&edit=${je.id}`)}>
                              <i className="fa-solid fa-pen"></i>
                            </button>
                            <button className="btn btn-sm btn-light tx-danger ms-1" title="Delete" onClick={() => handleDeleteRecent(je.id)}>
                              <i className="fa-solid fa-trash"></i>
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </AppContainer>
  );
};

export default NewJournalEntry;
